import { useTranslation } from '../../../../lib/i18n';
import { BAR_FRACTION, PasswordStrength } from '../../../../lib/password-strength';
import { Card, ProgressBar } from '../../../symphony/ui';
import styles from './change-password.module.css';

// The strength card under the new-password field: a classification label, one determinate bar filled
// to a classification-keyed fraction, the four-rule checklist, and the "avoid common passwords" note.
// Same rules, same wording and the same bar fractions as the native apps' `PasswordStrengthMeter`.
export function PasswordStrengthMeter({ strength }: { strength: PasswordStrength }) {
  const { t } = useTranslation();
  const { classification } = strength;

  return (
    <Card variant="outline">
      <div className={styles.meter}>
        <div className={styles.meterHead}>
          <span className={styles.strengthLabel}>{t(`settings.changePassword.strength.${classification}`)}</span>
          {/* Decorative: the label above and the checklist below already say this in text. */}
          <ProgressBar
            type="password"
            progress={classification}
            value={BAR_FRACTION[classification] * 100}
            decorative
          />
        </div>

        <ul className={styles.rules}>
          <Rule label={t('settings.changePassword.strength.letters')} met={strength.hasLetters} />
          <Rule label={t('settings.changePassword.strength.numbers')} met={strength.hasNumbers} />
          <Rule label={t('settings.changePassword.strength.symbols')} met={strength.hasSymbols} />
          <Rule label={t('settings.changePassword.strength.minLength')} met={strength.meetsMinLength} />
        </ul>

        <span className={styles.hint}>{t('settings.changePassword.strength.avoidCommon')}</span>
      </div>
    </Card>
  );
}

function Rule({ label, met }: { label: string; met: boolean }) {
  return (
    <li className={[styles.rule, !met && styles.unmet].filter(Boolean).join(' ')}>
      <span className={styles.ruleIcon} aria-hidden>
        {met ? <CheckIcon /> : <MinusIcon />}
      </span>
      {label}
    </li>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M3.33 8.33 6 11l6.67-6.67" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M3.33 8h9.34" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
